import { api, apiUrl } from "../../../shared/services/service";
import {
  VesselCardApiResponse,
  VesselCardDashboardResponse,
  VesselCardDetail,
  VesselCardIndicatorTone,
  VesselCardVesselsResponse,
} from "../models/vessel-card.models";

const display = (value: string | number | boolean | null, suffix = ""): string =>
  value === null ? "—" : `${typeof value === "boolean" ? (value ? "Yes" : "No") : value}${suffix}`;

const detail = (label: string, value: string | number | boolean | null, suffix = ""): VesselCardDetail => ({
  label,
  value: display(value, suffix),
});

const toIndicatorTone = (indicator: string | null): VesselCardIndicatorTone => {
  const normalized = indicator?.toLowerCase() ?? "";

  if (
    normalized.includes("under") ||
    normalized.includes("below") ||
    normalized.includes("outside") ||
    normalized.includes("worse")
  ) {
    return "negative";
  }

  if (normalized.includes("marginally")) {
    return "neutral";
  }

  if (normalized.includes("better")) {
    return "positive";
  }

  if (normalized.includes("within")) {
    return "warning";
  }

  return "neutral";
};

const toFoulingTone = (factor: string | null): VesselCardIndicatorTone => {
  const normalized = factor?.toLowerCase() ?? "";

  if (normalized.includes("high")) {
    return "negative";
  }

  if (normalized.includes("medium")) {
    return "warning";
  }

  if (normalized.includes("low")) {
    return "positive";
  }

  return "neutral";
};

// Adapts the Snowflake-oriented API aggregate into the stable page model also used by fixtures.
const toVesselCardDashboardResponse = (response: VesselCardApiResponse): VesselCardDashboardResponse => {
  const card = response.card;
  const vessel = response.selectedVessel;

  if (!card || !vessel) {
    throw new Error("No eligible Vessel Card data is available.");
  }

  const currentStatus = card.currentPerformance.status ?? "Performance status unavailable";

  return {
    vessels: response.vessels,
    card: {
      vessel,
      performanceBasis: card.overview.performanceBasis,
      evaluatedAt: card.overview.evaluatedDate,
      lastFixtureAt: card.overview.lastFixtureDate,
      // Missing scrubber information must not be presented as a confirmed No.
      scrubberFitted: card.overview.scrubberFitted,
      summerDwt: card.particulars.summerDwt,
      scrubberInformation: {
        scrubberImosType: card.overview.scrubberImosType,
        scrubberImosFitted: card.overview.scrubberImosFitted,
        scrubberPerformanceSummaryFitted: card.overview.scrubberPerformanceSummaryFitted,
        scrubberConflict: card.overview.scrubberConflict,
      },
      performance: card.performance.map((row) => ({
        mode: row.label,
        expectedSpeed: row.expectedPerformance.speed,
        expectedConsumption: row.expectedPerformance.consumption,
        cpSpeed: row.cpDescription.speed,
        // An older API's combined fuel total must not seed Custom CP defaults either.
        cpConsumption: row.cpDescription.consumptionByFuel ? row.cpDescription.consumption : null,
        cpConsumptionByFuel: row.cpDescription.consumptionByFuel,
        expectedAtCpSpeed: row.expectedAtCpConsumption.speed,
        expectedAtCpConsumption: row.expectedAtCpConsumption.consumption,
        indicator: row.cpPerformanceIndicator ?? "Not available",
        indicatorTone: toIndicatorTone(row.cpPerformanceIndicator),
      })),
      currentPerformance: {
        label: currentStatus,
        speedDifference: card.currentPerformance.speedDeltaKnots,
        tone: toIndicatorTone(currentStatus),
        lastHullInspectionOrCleaning: card.currentPerformance.lastHullInspectionOrCleaning,
        lastDryDock: card.currentPerformance.lastDryDock,
      },
      foulingFactor: card.fouling.factor,
      foulingTone: toFoulingTone(card.fouling.factor),
      foulingDetails:
        card.fouling.exposure === null
          ? "The semantic-model fouling band is unavailable."
          : `Raw fouling exposure: ${card.fouling.exposure}`,
      tcoDescriptions: card.tco.map((row) => ({
        mode: row.label,
        aggressiveSpeed: row.aggressive.speed,
        aggressiveConsumption: row.aggressive.consumption,
        veryAggressiveSpeed: row.veryAggressive.speed,
        veryAggressiveConsumption: row.veryAggressive.consumption,
      })),
      comments: card.comments.map((comment) => comment.comment),
      vesselParticulars: [
        detail("Vessel Type", card.particulars.vesselType),
        detail(
          "Built / Age",
          card.particulars.builtYear === null
            ? null
            : `${card.particulars.builtYear} / ${display(card.particulars.ageYears, " years")}`,
        ),
        detail("Summer DWT", card.particulars.summerDwt, " MT"),
        detail("Summer Draft", card.particulars.summerDraft, " m"),
        detail("LOA", card.particulars.loa, " m"),
        detail("Beam", card.particulars.beam, " m"),
        detail("TPC", card.particulars.tpc, " t/cm"),
        detail("Grain Capacity", card.particulars.grainCapacity, " m³"),
      ],
      engineAndDesign: [
        detail("Engine Type", card.engineAndDesign.engineType),
        detail("Yard", card.engineAndDesign.yard),
        detail("Country Built", card.engineAndDesign.countryBuilt),
        detail("Design", card.engineAndDesign.design),
        detail("Scrubber Fitted", card.engineAndDesign.scrubberFitted),
        detail(
          "Holds / Hatches",
          card.engineAndDesign.holds === null && card.engineAndDesign.hatches === null
            ? null
            : `${display(card.engineAndDesign.holds)} / ${display(card.engineAndDesign.hatches)}`,
        ),
        detail(
          "Int. Gross / Net",
          card.engineAndDesign.grossTonnage === null && card.engineAndDesign.netTonnage === null
            ? null
            : `${display(card.engineAndDesign.grossTonnage)} / ${display(card.engineAndDesign.netTonnage)}`,
        ),
      ],
      rightShip: [
        detail(
          "Safety Score / GHG",
          card.rightShip.safetyScore === null && card.rightShip.ghgRating === null
            ? null
            : `${display(card.rightShip.safetyScore)} / ${display(card.rightShip.ghgRating)}`,
        ),
        detail("D.O.C Score", card.rightShip.documentOfComplianceScore),
        detail("RS Last Outcome", card.rightShip.lastInspectionOutcome),
        detail("RS Inspection Valid To", card.rightShip.inspectionValidTo),
        detail("Flag", card.rightShip.flag),
      ],
      ownership: [
        detail(
          "Beneficial Owner",
          card.ownership.beneficialOwner === null
            ? null
            : `${card.ownership.beneficialOwner}${card.ownership.beneficialOwnerCountry ? ` | ${card.ownership.beneficialOwnerCountry}` : ""}`,
        ),
        detail(
          "Registered Owner",
          card.ownership.registeredOwner === null
            ? null
            : `${card.ownership.registeredOwner}${card.ownership.registeredOwnerCountry ? ` | ${card.ownership.registeredOwnerCountry}` : ""}`,
        ),
        detail(
          "Ship Manager",
          card.ownership.shipManager === null
            ? null
            : `${card.ownership.shipManager}${card.ownership.shipManagerCountry ? ` | ${card.ownership.shipManagerCountry}` : ""}`,
        ),
      ],
      idleStays: card.idleStays
        .map((stay) => ({
          ...stay,
          foulingExposure: stay.foulingExposure === null ? null : String(stay.foulingExposure),
        }))
        .sort((left, right) => (right.idleTo ?? "").localeCompare(left.idleTo ?? "")),
    },
  };
};

// Loads the complete card in one request because every panel shares the selected vessel and evaluation basis.
export const getVesselCardDashboard = async (
  imo: string | null,
  signal: AbortSignal,
): Promise<VesselCardDashboardResponse> => {
  const response = await api.get<VesselCardApiResponse>(`${apiUrl}/pbi/vessel-card`, {
    params: imo ? { imo } : undefined,
    signal,
  });

  return toVesselCardDashboardResponse(response.data);
};

// Loads one bounded selector page so live search and scrolling never download the complete IHS fleet.
export const getVesselCardVessels = async (
  query: string,
  limit: number,
  offset: number,
  signal: AbortSignal,
): Promise<VesselCardVesselsResponse> => {
  const response = await api.get<VesselCardVesselsResponse>(`${apiUrl}/pbi/vessel-card/vessels`, {
    params: { ...(query ? { q: query } : {}), limit, offset },
    signal,
  });

  return response.data;
};
